import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, openErrorModal } from '@vario-connect/frontend-design';
import { renderListAdditionalFields } from '../tabs/list-additional-fields.js';
import { renderVariantDataGrid } from '../tabs/variant-data.js';
import { renderImagesTab } from '../tabs/list-images.js';
import { renderListPreview } from '../tabs/list-preview.js';
import { renderPerArticleMapping } from '../tabs/list-mapping.js';
import { getOverviewSaves } from '../../lib/overview-save-registry.js';
import { markTabDirty, clearTabDirty, isTabDirty, confirmDiscardChanges } from '../../lib/tab-dirty.js';
import { varioArticleUrl } from '../../lib/vario-links.js';

// ============================================================================
// Geteiltes Gambio-Listing-Detail-Pane - gerendert vom Lister (Master-Detail,
// rechte Spalte) UND vom Vario-Slot listing-status (Configure-Modus im Artikel).
//
// Gambio-Modell (Spec 2026-06-25, Fork von Otto):
// Tabs: Übersicht, Mapping, Zusatzfelder, Varianten, Bilder, Vorschau.
// Übersicht = Kategorie (Verkaufskanal, read-only aus Vario) + Darstellung/Google-Shopping/
//             Artikel-Mapping mit EINEM „Speichern"-Button. Archivieren nur im Standalone-Lister.
//             (Der frühere „Als push-bereit markieren"-Button ist entfallen: der Aktiv-Schalter
//              + direktes Übertragen im Slot ersetzen ihn.)
// Mapping/Zusatzfelder/Varianten/Bilder/Vorschau: STUB (folgen in Tasks 14-17).
// Kein Brand-Picker (OTTO-spezifisch). Kein GPSR-Tab.
// ============================================================================

/** Zweistufige Bestätigung (Projektregel: keine window.confirm/alert).
 *  Erster Klick „schärft" den Button, nach 4s ohne zweiten Klick Reset. */
export function wireTwoStepConfirm(
  btn: HTMLButtonElement,
  armedHtml: string,
  onConfirm: () => void,
): void {
  const idleHtml = btn.innerHTML;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const disarm = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    btn.dataset['armed'] = '';
    btn.innerHTML = idleHtml;
    btn.classList.remove('klst-btn-armed');
  };
  btn.addEventListener('click', () => {
    if (btn.dataset['armed'] === '1') { disarm(); onConfirm(); return; }
    btn.dataset['armed'] = '1';
    btn.innerHTML = armedHtml;
    btn.classList.add('klst-btn-armed');
    timer = setTimeout(disarm, 4000);
  });
}

export interface DetailPaneCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
  supabaseUrl: string;
}

export interface DetailPaneHost {
  kind: 'lister' | 'slot';
  onListingChanged(): void;
  onDeleted(): void;
}

type DetailTab = 'overview' | 'additional' | 'variants' | 'images' | 'preview';

export type ListingStatus = 'pending' | 'mapping' | 'mapped' | 'error' | 'archived';

export const STATUS_PILL: Record<ListingStatus, { cls: string; label: string }> = {
  pending:  { cls: 'v-pill--info',  label: 'Neu' },
  mapping:  { cls: 'v-pill--warn',  label: 'In Bearbeitung' },
  mapped:   { cls: 'v-pill--ok',    label: 'Bereit' },
  error:    { cls: 'v-pill--error', label: 'Fehler' },
  archived: { cls: 'v-pill--muted', label: 'Archiviert' },
};

export interface ListingDetail {
  id: string;
  storefront: string | null;
  vario_article_id: string;
  vario_listing_id: string;
  vario_article_number: string | null;
  vario_article_name: string | null;
  vario_category_id: string | null;
  vario_category_name: string | null;
  gambio_category_id: string | null;
  vario_listing_snapshot: Record<string, unknown> | null;
  is_variant_parent: boolean;
  is_variant_child: boolean;
  parent_listing_id: string | null;
  mapping_override: Record<string, unknown> | null;
  status: ListingStatus;
  last_error: string | null;
  first_seen_at: string;
  last_scan_at: string;
  updated_at: string;
}

// ============================================================================
// Entry-Funktion
// ============================================================================

export async function renderListingDetailPane(
  mount: HTMLElement,
  ctx: DetailPaneCtx,
  listingId: string,
  paneHost: DetailPaneHost,
): Promise<void> {
  mount.innerHTML = `<div class="klst-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Details…</div>`;
  const { data, error } = await ctx.supabase.rpc('gambio_get_listing_detail', { _id: listingId });
  if (error) {
    mount.innerHTML = `<div class="klst-loading klst-loading--error">Fehler: ${escapeHtml(error.message)}</div>`;
    return;
  }
  const detail = (Array.isArray(data) ? data[0] : data) as ListingDetail | null | undefined;
  if (!detail) {
    mount.innerHTML = `<div class="klst-loading">Listing nicht gefunden.</div>`;
    return;
  }

  // Variantenkind → reduzierter Lister (Kategorie vom Vater, nur variantenmögliche Felder).
  if (detail.is_variant_child) {
    // Vater-Artikel-URL auflösen: Der „Zum Hauptartikel"-Link muss VARIO komplett auf den
    // Vaterartikel navigieren (target="_top"), nicht nur das iframe-Pane tauschen — sonst zeigt
    // der Vario-Rahmen weiter das Kind (Nutzer-Verwirrung, 2026-07-23). Dafür die Vario-Artikel-ID
    // des Vaters über das Parent-Listing auflösen (eigener RPC-Read, best-effort → sonst kein Link).
    let parentUrl: string | null = null;
    if (detail.parent_listing_id) {
      try {
        const { data: pData } = await ctx.supabase.rpc('gambio_get_listing_detail', { _id: detail.parent_listing_id });
        const parent = (Array.isArray(pData) ? pData[0] : pData) as ListingDetail | null | undefined;
        parentUrl = varioArticleUrl(parent?.vario_article_id ?? null);
      } catch { /* best-effort: kein Link */ }
    }
    mount.innerHTML = renderChildDetail(detail, parentUrl);
    wireChildPane(mount, ctx, detail, paneHost);
    return;
  }

  mount.innerHTML = renderDetail(detail, paneHost.kind);
  wireDetailTabs(mount, ctx, detail, paneHost);

  // Archivieren gibt es nur im Standalone-Lister (Listen-/Detail-Ansicht). Im Artikel-Slot
  // (Vario-Verkaufskanal) ist Archivieren fachlich sinnlos - dort steuert der Händler den Artikel
  // direkt über Vario. renderDetail rendert den Button daher nur für kind==='lister'.
  const archiveBtn = mount.querySelector<HTMLButtonElement>('#klst-detail-archive');
  if (archiveBtn) {
    wireTwoStepConfirm(
      archiveBtn,
      detail.status === 'archived'
        ? '<span class="v-icon fa-regular fa-rotate-left"></span> Wirklich wiederaufnehmen?'
        : '<span class="v-icon fa-regular fa-box-archive"></span> Wirklich archivieren?',
      () => void archiveListing(detail.id, detail.status !== 'archived'),
    );
  }

  // EIN „Speichern" für den ganzen Übersicht-Tab: ruft alle Sektions-Speicher (Darstellung,
  // Google-Shopping, Artikel-Mapping) auf, die sich via overview-save-registry eingetragen haben.
  const saveAllBtn = mount.querySelector<HTMLButtonElement>('#gambio-overview-save-all');
  saveAllBtn?.addEventListener('click', () => {
    const sections = getOverviewSaves(saveAllBtn);
    if (sections.length === 0) { showToast('Nichts zu speichern.', 'info'); return; }
    const orig = saveAllBtn.innerHTML;
    saveAllBtn.setAttribute('disabled', '');
    saveAllBtn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Speichern…';
    void (async () => {
      // NACHEINANDER, nicht parallel: „Darstellung" und „Google-Shopping" schreiben auf DASSELBE
      // Gambio-Produkt, beide über GET /products/{id} → mergen → PUT des ganzen Produkts. Parallel
      // gestartet lesen sie denselben Stand und der zweite PUT überschreibt still die Änderung des
      // ersten; Gambio quittiert das Nebeneinander zusätzlich mit HTTP 500 „Duplicate entry '<id>'
      // for key 'products_quantity_unit.PRIMARY'", weil es die Mengeneinheit-Zeile je PUT löscht
      // und neu einfügt (Vorfall 2026-09-22, Pfannenshop, Produkt 80). Sequenziell liest jede
      // Sektion den bereits gespeicherten Stand der vorherigen.
      const results: Array<{ ok: boolean; msg?: string }> = [];
      for (const s of sections) {
        try {
          results.push(await s.run());
        } catch (e) {
          results.push({ ok: false, msg: `${s.label}: ${(e as Error).message}` });
        }
      }
      const failed = results.filter((r) => !r.ok);
      if (failed.length === 0) {
        showToast('Gespeichert.', 'success');
        // Übersicht-Tab ist jetzt persistiert → „ungespeichert"-Marker löschen (Tab-Wechsel-Guard).
        clearTabDirty(saveAllBtn);
      } else {
        openErrorModal({
          title: 'Speichern fehlgeschlagen',
          items: failed.map((f) => ({ message: f.msg ?? 'Unbekannter Fehler' })),
          context: { action: 'gambio-overview-save-all', listingId: detail.id, sections: sections.map((s) => s.label) },
          json: failed,
        });
      }
      saveAllBtn.innerHTML = orig;
      saveAllBtn.removeAttribute('disabled');
    })();
  });

  const mapHost = mount.querySelector<HTMLElement>('#gambio-article-mapping-editor');
  if (mapHost) void renderPerArticleMapping(mapHost, { supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: ctx.session }, { listingId: detail.id, varioArticleId: detail.vario_article_id });

  async function archiveListing(id: string, archive: boolean) {
    const { error: archErr } = await ctx.supabase.rpc('gambio_archive_listing', { _id: id, _archive: archive });
    if (archErr) {
      openErrorModal({
        title: 'Archivieren fehlgeschlagen',
        items: [{ message: archErr.message }],
        context: { action: 'gambio_archive_listing', listingId: id, archive },
        json: archErr,
      });
      return;
    }
    showToast(archive ? 'Listing archiviert.' : 'Listing wiederaufgenommen.', 'success');
    paneHost.onListingChanged();
  }
}

// ============================================================================
// Tab-Wiring
// ============================================================================

function wireDetailTabs(mount: HTMLElement, ctx: DetailPaneCtx, detail: ListingDetail, _host: DetailPaneHost): void {
  const tabsBar = mount.querySelector<HTMLElement>('#klst-detail-tabs');
  const panes = mount.querySelectorAll<HTMLElement>('[data-detail-pane]');
  if (!tabsBar) return;

  const switchTo = (target: DetailTab) => {
    tabsBar.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((b) => {
      b.classList.toggle('klst-tab--active', b.dataset['detailTab'] === target);
    });
    panes.forEach((p) => { p.hidden = p.dataset['detailPane'] !== target; });

    const tabHost = mount.querySelector<HTMLElement>(`[data-detail-pane="${target}"]`);
    if (!tabHost) return;

    if (target === 'variants') {
      // Kein Variantenartikel → Info-Banner statt Grid.
      if (!detail.is_variant_parent) {
        if (!tabHost.dataset['loaded']) {
          tabHost.dataset['loaded'] = '1';
          tabHost.innerHTML = `<div class="v-pa-md"><div class="v-banner v-banner-info">
            <span class="v-icon fa-light fa-circle-info"></span>
            <div>Dieser Artikel ist kein Variantenhauptartikel - Varianten-Tab nicht verfügbar.</div>
          </div></div>`;
        }
        return;
      }
      // Immer neu laden (Kind-Daten können sich ändern).
      tabHost.dataset['loaded'] = '1';
      tabHost.innerHTML = '';

      const gridSection = document.createElement('div');
      tabHost.appendChild(gridSection);

      void renderVariantDataGrid(gridSection, { supabaseUrl: ctx.supabaseUrl, session: ctx.session }, {
        listingId: detail.id,
      });
      return;
    }

    if (target === 'additional') {
      tabHost.dataset['loaded'] = '1';
      void renderListAdditionalFields(tabHost, { supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: ctx.session }, {
        storefront: detail.storefront ?? '',
        gambioCategoryId: detail.gambio_category_id,
        varioArticleId: detail.vario_article_id,
        listingId: detail.id,
      });
      return;
    }

    if (target === 'images') {
      if (!tabHost.dataset['loaded']) {
        tabHost.dataset['loaded'] = '1';
        void renderImagesTab(
          tabHost,
          { supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token } },
          {
            vario_article_id: detail.vario_article_id,
            mode: detail.is_variant_parent ? 'parent' : 'standalone',
            parentListingId: detail.id,
          },
        );
      }
      return;
    }

    if (target === 'preview') {
      // Immer neu laden damit die Vorschau stets aktuell ist.
      tabHost.dataset['loaded'] = '1';
      renderListPreview(tabHost, { supabaseUrl: ctx.supabaseUrl, session: ctx.session }, { listingId: detail.id });
      return;
    }

    // Alle übrigen bekannten Tabs (overview wurde oben schon behandelt).
    if (target !== 'overview') {
      if (!tabHost.dataset['loaded']) {
        tabHost.dataset['loaded'] = '1';
        tabHost.innerHTML = renderTabStub(target);
      }
    }
  };

  // ── Tab-Wechsel-Guard bei ungespeicherten Änderungen ─────────────────────────────────────────
  // Markiert wird in JEDEM Reiter (User-Anforderung 2026-08-20, Testfall 12.12). Zusatzfelder und
  // Varianten werden bei jeder Aktivierung neu gerendert, verlieren die Eingaben also schon beim
  // Zurueckwechseln; Uebersicht/Bilder bleiben im DOM, verlieren sie aber, sobald die Detailansicht
  // ganz verlassen wird. Eine Warnung, die nur in zwei von fuenf Reitern kommt, ist nicht
  // verlaesslich — deshalb hier ohne Ausnahme. Die Tab-Module loeschen den Marker nach
  // erfolgreichem Speichern/Zuruecksetzen.
  const wrapper = mount.firstElementChild as HTMLElement | null;
  const markAny = (ev: Event): void => {
    const pane = (ev.target as HTMLElement | null)?.closest<HTMLElement>('[data-detail-pane]');
    if (pane) markTabDirty(pane);
  };
  wrapper?.addEventListener('input', markAny);
  wrapper?.addEventListener('change', markAny);

  const guardedSwitch = (target: DetailTab): void => {
    const cur = mount.querySelector<HTMLElement>('[data-detail-pane]:not([hidden])');
    if (!cur || cur.dataset['detailPane'] === target || !isTabDirty(cur)) { switchTo(target); return; }
    // Gemeinsamer Dialog aus lib/tab-dirty — identischer Text wie in den Einstellungen.
    confirmDiscardChanges(() => { clearTabDirty(cur); switchTo(target); }, 'Tab');
  };

  tabsBar.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((btn) => {
    btn.addEventListener('click', () => guardedSwitch(btn.dataset['detailTab'] as DetailTab));
  });
}

function renderTabStub(tab: DetailTab): string {
  const labels: Record<DetailTab, string> = {
    overview:   'Übersicht & Mapping',
    additional: 'Zusatzfelder',
    variants:   'Varianten',
    images:     'Bilder',
    preview:    'Vorschau',
  };
  return `<div class="v-pa-md"><div class="v-banner v-banner-info">
    <span class="v-icon fa-light fa-circle-info"></span>
    <div><strong>${escapeHtml(labels[tab] ?? tab)}</strong> - folgt in einem späteren Task.</div>
  </div></div>`;
}

// ============================================================================
// Render-Markup
// ============================================================================

function renderDetail(d: ListingDetail, kind: DetailPaneHost['kind']): string {
  const archiveLabel = d.status === 'archived' ? 'Wiederaufnehmen' : 'Archivieren';
  // Archivieren nur im Standalone-Lister anbieten - im Artikel-Slot nicht benötigt.
  const showArchive = kind === 'lister';
  return `
    <div>
      <div id="klst-detail-tabs" class="klst-tabs">
        <button type="button" class="klst-tab klst-tab--active" data-detail-tab="overview">
          <span class="v-icon fa-regular fa-sliders"></span> Übersicht &amp; Mapping
        </button>
        ${d.is_variant_parent ? `
        <button type="button" class="klst-tab" data-detail-tab="variants">
          <span class="v-icon fa-regular fa-list-check"></span> Varianten
        </button>` : ''}
        <button type="button" class="klst-tab" data-detail-tab="additional">
          <span class="v-icon fa-regular fa-list-ul"></span> Zusatzfelder
        </button>
        <button type="button" class="klst-tab" data-detail-tab="images">
          <span class="v-icon fa-regular fa-images"></span> Bilder
        </button>
        <button type="button" class="klst-tab" data-detail-tab="preview">
          <span class="v-icon fa-regular fa-eye"></span> Vorschau
        </button>
      </div>

      <!-- Übersicht: Kategorie + Status-Aktionen. -->
      <div data-detail-pane="overview">
        <div class="v-pa-md" style="padding-bottom:4px;">
          <div class="v-row v-row-sm klst-detail-actions">
            ${showArchive ? `<div class="v-col-shrink">
              <button type="button" class="v-button" id="klst-detail-archive">
                <span class="v-icon fa-regular fa-box-archive"></span> ${escapeHtml(archiveLabel)}
              </button>
            </div>` : ''}
            <div class="v-col-shrink" style="margin-left:auto;">
              <button type="button" class="v-button v-button--save" id="gambio-overview-save-all">
                <span class="v-icon fa-regular fa-floppy-disk"></span> Speichern
              </button>
            </div>
          </div>

          ${d.last_error ? `<div class="v-banner v-banner-danger klst-detail-error">
            <span class="v-icon fa-light fa-circle-xmark"></span>
            <div style="white-space:pre-wrap;"><strong>Letzter Fehler</strong>\n${escapeHtml(d.last_error)}</div></div>` : ''}

          <table class="klst-detail-table"><tbody>
            <tr><th>Artikel</th><td>${d.vario_article_name ? `<strong>${escapeHtml(d.vario_article_name)}</strong>` : '<span class="v-text-muted">—</span>'}
              ${d.vario_article_number ? ` <span class="v-text-muted">(${escapeHtml(d.vario_article_number)})</span>` : ''}</td></tr>
            <tr><th>Status</th><td><span class="v-pill ${STATUS_PILL[d.status]?.cls ?? 'v-pill--info'}">${escapeHtml(STATUS_PILL[d.status]?.label ?? d.status)}</span></td></tr>
          </tbody></table>
          <!-- Kategorien-Block (Picker + „Kategorien aktualisieren") wird von listing-status.ts
               nach dem Render hier eingehängt (loadCategoryPicker → paneEl #go-shop-categories). -->
          <div id="go-shop-categories" style="margin-top:24px;"></div>
          <div id="go-display-settings" style="margin-top:24px;"></div>
          <div id="go-google-shopping" style="margin-top:24px;margin-bottom:10px;"></div>
          <div id="gambio-article-mapping-editor" style="margin-top:24px;"></div>
        </div>
      </div>

      ${d.is_variant_parent ? `<div data-detail-pane="variants" hidden></div>` : ''}
      <div data-detail-pane="additional" hidden></div>
      <div data-detail-pane="images" hidden></div>
      <div data-detail-pane="preview" hidden></div>
    </div>`;
}

// ============================================================================
// Reduzierter Kind-Lister (Variantenkind)
// ============================================================================

function renderChildDetail(d: ListingDetail, parentUrl: string | null): string {
  // target="_top": navigiert die GANZE Vario-Oberfläche zum Vaterartikel (nicht nur das iframe-Pane),
  // damit der Vario-Rahmen den Vaterartikel vollständig lädt. Fehlt die URL (Host/ID nicht auflösbar),
  // kein Link.
  return `
    <div>
      <div class="v-banner v-banner-info" style="margin-bottom:10px;">
        <span class="v-icon fa-light fa-circle-info"></span>
        <div>${d.vario_article_number
          ? `Variante <strong>${escapeHtml(d.vario_article_number)}</strong>`
          : 'Diese Variante'} —
        Kategorie wird im <strong>Variantenhauptartikel</strong> gepflegt. Hier nur die
        variantenspezifischen Angaben.${parentUrl
          ? ` <a href="${escapeHtml(parentUrl)}" target="_top" rel="noopener"><span class="v-icon fa-regular fa-arrow-up-right-from-square"></span> Zum Variantenhauptartikel</a>`
          : ''}</div>
      </div>

      <div id="klst-detail-tabs" class="klst-tabs">
        <button type="button" class="klst-tab klst-tab--active" data-detail-tab="overview">
          <span class="v-icon fa-regular fa-tag"></span> EAN &amp; Preis
        </button>
        <button type="button" class="klst-tab" data-detail-tab="images">
          <span class="v-icon fa-regular fa-images"></span> Bilder
        </button>
      </div>

      <div data-detail-pane="overview">
        <div class="v-pa-md">
          <table class="klst-detail-table"><tbody>
            <tr><th>Kategorie (Verkaufskanal)</th><td>
              ${d.vario_category_name
                ? `<strong>${escapeHtml(d.vario_category_name)}</strong>`
                : d.vario_category_id
                  ? `<span class="v-text-muted">${escapeHtml(d.vario_category_id)}</span>`
                  : '<span class="v-text-muted">— im Variantenhauptartikel —</span>'}
              <span class="v-text-muted" style="font-size:11px;">(vom Variantenhauptartikel)</span>
            </td></tr>
            <tr><th>EAN</th><td>
              <input class="v-input" id="gambio-child-ean" type="text" inputmode="numeric" autocomplete="off" placeholder="EAN (GS1, 8/13-stellig)" style="width:180px;" />
              <div id="gambio-child-ean-hint" class="v-text-muted" style="font-size:11px;height:14px;"></div>
            </td></tr>
            <tr><th>Preis (€)</th><td>
              <input class="v-input" id="gambio-child-price" type="text" inputmode="decimal" autocomplete="off" placeholder="0,00" style="width:120px;" />
            </td></tr>
          </tbody></table>
          <div class="v-row v-row-sm" style="align-items:center;gap:10px;margin-top:8px;">
            <button type="button" class="v-button v-button--save" id="gambio-child-save">
              <span class="v-icon fa-regular fa-floppy-disk"></span> Nach Vario/Gambio speichern
            </button>
            <span id="gambio-child-save-status" class="v-text-muted" style="font-size:12px;"></span>
          </div>
        </div>
      </div>
      <div data-detail-pane="images" hidden></div>
    </div>`;
}

function wireChildPane(mount: HTMLElement, ctx: DetailPaneCtx, detail: ListingDetail, host: DetailPaneHost): void {
  // Der „Zum Hauptartikel"-Link ist ein nativer <a target="_top"> (siehe renderChildDetail) — Vario
  // navigiert dadurch komplett auf den Vaterartikel; kein JS-Handler / kein In-App-Pane-Wechsel nötig.
  void host;
  const tabsBar = mount.querySelector<HTMLElement>('#klst-detail-tabs');
  const panes = mount.querySelectorAll<HTMLElement>('[data-detail-pane]');

  const switchTo = (target: string) => {
    tabsBar?.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((b) => {
      b.classList.toggle('klst-tab--active', b.dataset['detailTab'] === target);
    });
    panes.forEach((p) => { p.hidden = p.dataset['detailPane'] !== target; });

    const paneHost = mount.querySelector<HTMLElement>(`[data-detail-pane="${target}"]`);

    if (target === 'images' && paneHost && !paneHost.dataset['loaded']) {
      paneHost.dataset['loaded'] = '1';
      // Variante: editierbare Matrix-Spalte (gemeinsamer Pool, Zuweisung zu DIESER
      // Variante). Ohne verknuepften Vater faellt es auf die eigenstaendige Liste zurueck.
      const imagesMode: 'variant' | 'standalone' = detail.parent_listing_id ? 'variant' : 'standalone';
      void renderImagesTab(
        paneHost,
        { supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, session: { token: ctx.session.token } },
        {
          vario_article_id: detail.vario_article_id,
          mode: imagesMode,
          parentListingId: detail.parent_listing_id,
        },
      );
      return;
    }

    // Stubs für alle anderen Kind-Tabs
    if (target !== 'overview' && paneHost && !paneHost.dataset['loaded']) {
      paneHost.dataset['loaded'] = '1';
      paneHost.innerHTML = renderTabStub(target as DetailTab);
    }
  };
  tabsBar?.querySelectorAll<HTMLButtonElement>('button[data-detail-tab]').forEach((btn) => {
    btn.addEventListener('click', () => switchTo(btn.dataset['detailTab']!));
  });

  const eanInp = mount.querySelector<HTMLInputElement>('#gambio-child-ean');
  const priceInp = mount.querySelector<HTMLInputElement>('#gambio-child-price');
  const hint = mount.querySelector<HTMLElement>('#gambio-child-ean-hint');
  const statusEl = mount.querySelector<HTMLElement>('#gambio-child-save-status');
  const saveBtn = mount.querySelector<HTMLButtonElement>('#gambio-child-save');

  // Aktuellen Stand (EAN/Preis) über gambio-variant-rows (Vater) laden und vorbefüllen.
  if (detail.parent_listing_id) {
    void childCallFn<{ rows: Array<{ articleId: string; ean: string | null; priceEur: number | null }> }>(
      ctx, 'gambio-variant-rows', { action: 'list', listingId: detail.parent_listing_id },
    ).then((res) => {
      const row = (res.rows ?? []).find((r) => r.articleId === detail.vario_article_id);
      if (row) {
        if (eanInp && row.ean) eanInp.value = row.ean;
        if (priceInp && row.priceEur != null) priceInp.value = String(row.priceEur);
        paintEanHint();
      }
    }).catch(() => { /* still editierbar, nur ohne Vorbefüllung */ });
  }

  function paintEanHint(): void {
    if (!eanInp || !hint) return;
    const v = eanInp.value.trim();
    if (!v) { hint.textContent = ''; eanInp.style.borderColor = ''; return; }
    const r = childValidateEan(v);
    if (!r.ok) { hint.textContent = r.reason ?? 'ungültig'; hint.className = 'v-text-error'; eanInp.style.borderColor = 'var(--go-danger,#c0392b)'; }
    else { hint.textContent = '✓'; hint.className = 'v-text-success'; eanInp.style.borderColor = ''; }
  }
  eanInp?.addEventListener('input', paintEanHint);

  saveBtn?.addEventListener('click', async () => {
    if (!detail.parent_listing_id) { showToast('Kein Variantenhauptartikel verknüpft.', 'error'); return; }
    const ean = eanInp?.value.trim() ?? '';
    const priceStr = priceInp?.value.trim() ?? '';
    if (ean && !childValidateEan(ean).ok) { showToast('EAN ungültig - bitte korrigieren.', 'error'); return; }
    const row: { articleId: string; ean?: string; priceEur?: number } = { articleId: detail.vario_article_id };
    if (ean) row.ean = ean;
    if (priceStr) { const p = Number(priceStr.replace(',', '.')); if (Number.isFinite(p)) row.priceEur = p; }
    if (!row.ean && row.priceEur === undefined) { showToast('Nichts zu speichern.', 'info'); return; }

    saveBtn.disabled = true;
    if (statusEl) statusEl.textContent = 'Speichere…';
    try {
      const res = await childCallFn<{ results: Array<{ ean?: { ok: boolean; message?: string }; price?: { ok: boolean; message?: string } }> }>(
        ctx, 'gambio-variant-rows', { action: 'save', listingId: detail.parent_listing_id, rows: [row] },
      );
      const r = res.results?.[0];
      const ok = (r?.ean?.ok ?? true) && (r?.price?.ok ?? true);
      const parts = [
        r?.ean ? (r.ean.ok ? 'EAN ✓' : `EAN ✗ ${r.ean.message ?? ''}`) : null,
        r?.price ? (r.price.ok ? 'Preis ✓' : `Preis ✗ ${r.price.message ?? ''}`) : null,
      ].filter(Boolean).join(' · ');
      if (statusEl) { statusEl.textContent = parts || (ok ? 'gespeichert' : 'Fehler'); statusEl.className = ok ? 'v-text-success' : 'v-text-error'; }
      showToast(ok ? 'Variante gespeichert.' : 'Mit Fehler gespeichert - siehe Status.', ok ? 'success' : 'error');
    } catch (e) {
      openErrorModal({
        title: 'Speichern fehlgeschlagen',
        items: [{ message: (e as Error).message }],
        context: { action: 'gambio-variant-rows/save', listingId: detail.parent_listing_id, articleId: detail.vario_article_id },
        json: { message: (e as Error).message, stack: (e as Error).stack },
      });
      if (statusEl) statusEl.textContent = '';
    } finally {
      saveBtn.disabled = false;
    }
  });
}

async function childCallFn<T>(ctx: DetailPaneCtx, name: string, body: unknown): Promise<T> {
  const res = await fetch(`${ctx.supabaseUrl}/functions/v1/${name}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${ctx.session.token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    let msg = `${name} ${res.status}`;
    try { const j = JSON.parse(t); if (j?.error?.message) msg = j.error.message; } catch { /* keep */ }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

function childValidateEan(ean: string): { ok: boolean; reason?: string } {
  if (!/^\d+$/.test(ean)) return { ok: false, reason: 'nur Ziffern' };
  if (![8, 12, 13, 14].includes(ean.length)) return { ok: false, reason: `${ean.length} Stellen (8/13)` };
  const d = ean.split('').map(Number);
  const check = d.pop()!;
  let sum = 0;
  for (let i = d.length - 1, w = 3; i >= 0; i -= 1, w = w === 3 ? 1 : 3) sum += d[i]! * w;
  if ((10 - (sum % 10)) % 10 !== check) return { ok: false, reason: 'Prüfziffer' };
  return { ok: true };
}

// ============================================================================
// Utils
// ============================================================================

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

